import type { ReactNode } from 'react';
import { Text, Title } from '@repletoryreach/sdk-ui';
import './billing.css';

export interface TopListItem {
  id: string;
  label: ReactNode;
  /** The figure, already formatted. */
  value: ReactNode;
  /** How this row compares with the largest, from 0 to 1. */
  fraction: number;
}

/**
 * A ranked bar list: who spends the most, which SKU costs the most.
 *
 * The design puts a bar chart here, and a bar chart of ten labelled values is
 * a list with a length behind each row. Drawing it that way keeps the labels
 * selectable and readable at any width, which a canvas chart does not.
 */
export function TopList({ title, items, tone = 'var(--rr-color-error)', empty, footer }: {
  title: ReactNode;
  items: TopListItem[];
  /** The bar colour. Pass a token, never a literal. */
  tone?: string;
  empty: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <section className="rr-billing-panel">
      <Title order={5}>{title}</Title>
      {items.length === 0 ? (
        <Text role="bodySmall" c="var(--rr-color-text-muted)">{empty}</Text>
      ) : (
        <ol className="rr-billing-toplist">
          {items.map((item) => (
            <li key={item.id} className="rr-billing-toplist__row">
              <span className="rr-billing-toplist__label">
                <Text role="bodySmall" lineClamp={1}>{item.label}</Text>
              </span>
              <span className="rr-billing-toplist__track" aria-hidden>
                <span
                  className="rr-billing-toplist__bar"
                  style={{ width: `${Math.max(2, Math.round(item.fraction * 100))}%`, background: tone }}
                />
              </span>
              <span className="rr-billing-toplist__value">
                <Text role="bodySmall" fw={600}>{item.value}</Text>
              </span>
            </li>
          ))}
        </ol>
      )}
      {footer && <Text role="caption" c="var(--rr-color-text-muted)">{footer}</Text>}
    </section>
  );
}
